import { apiClient } from "@/api/client";
import { Options } from "@/components/ui/DropDown";
import { AxiosError } from "axios";
import { create } from "zustand";

interface CampaignQueryParams {
  q?: string;
  client_team?: string;
  sort?: string;
  per_page?: number;
  page?: number;
}

interface PaginationMeta {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

interface DashboardState {
  campaignData: Array<CampaignData>;
  meta: PaginationMeta | null;
  clientTeamOptions: Options[];
  searchQuery: string;
  selectedClientTeam: string;

  addCampaignData: (newData: CampaignData) => void;
  setSearchQuery: (query: string) => void;
  setSelectedClientTeam: (teamId: string) => void;

  getCampaign: (params?: CampaignQueryParams) => Promise<void>;
  fetchClientTeams: () => Promise<void>;
}

export interface CampaignDetailResponse {
  success: boolean;
  data: CampaignData;
}

export interface CampaignResponse {
  success: boolean;
  data: CampaignData[];
  meta?: PaginationMeta;
}

export interface StatusCounts {
  briefed: number;
  in_progress: number;
  in_review: number;
  approved: number;
  trafficking: number;
  live: number;
  draft: number;
  total: number;
}

export interface CampaignData {
  campaign: Campaign;
  teams: Team[];
  users: User[];
  languages: Language[];
  status_counts?: StatusCounts;
  header_image?: {
    id: string;
    key: string | null;
    mime: string;
    type: string;
    url: string;
  };
}

export interface Campaign {
  id: string;
  name: string;
  project_code: string;
  budget: number;
  file_naming_convention: string;
  campaign_poster: string | null;
  status: string;
  progress_status: "campaign" | "flight" | "assets" | "done" | "brief" | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface Team {
  campaign_team_id: string;
  team_id: string;
  name: string;
  team_type: "client" | "media" | "creative" | string;
  all_users: User[];
  campaign_users: User[];
}

export interface User {
  team_id: string;
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  profile_picture: string | null;
}

export interface Language {
  campaign_language_id: string;
  language_id: string;
  name: string;
}

const useDashboardStore = create<DashboardState>((set, get) => ({
  campaignData: [],
  meta: null,
  clientTeamOptions: [],
  searchQuery: "",
  selectedClientTeam: "",

  addCampaignData: (newData: CampaignData) => {
    set((state) => ({
      campaignData: [...state.campaignData, newData],
    }));
  },

  setSearchQuery: (query: string) => set({ searchQuery: query }),

  setSelectedClientTeam: (teamId: string) => set({ selectedClientTeam: teamId }),

  getCampaign: async (params?: CampaignQueryParams) => {
    try {
      const queryParams = new URLSearchParams();

      if (params?.q) queryParams.append("q", params.q);
      if (params?.client_team) queryParams.append("client_team", params.client_team);
      if (params?.sort) queryParams.append("sort", params.sort);
      if (params?.per_page) queryParams.append("per_page", params.per_page.toString());
      if (params?.page) queryParams.append("page", params.page.toString());

      const url = `/campaigns${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
      const response = await apiClient.get<CampaignResponse>(url);
      const data = response.data;

      set({
        campaignData: data.data,
        meta: data.meta || null,
      });
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.error || "Something went wrong"
        : "Something went wrong";
      throw new Error(errorMessage);
    }
  },

  fetchClientTeams: async () => {
    try {
      const response = await apiClient.get("/client-teams");
      const teams = response.data?.data || response.data || [];
      const options: Options[] = teams.map((team: any) => ({
        title: team.name,
        value: team.id,
      }));
      set({ clientTeamOptions: options });
    } catch (error) {
      // Silently fail — dropdown will just be empty
      set({ clientTeamOptions: [] });
    }
  },
}));

export default useDashboardStore;
